/**
 * Page Player
 * Renders a page with sections and content blocks
 */

import * as React from 'react';
import { cn, Card, CardHeader, CardTitle, CardContent } from '@ai-content-hub/content-kit';
import { LayoutContainer } from '@ai-content-hub/content-core';
import { BlockRenderer } from '../block/BlockRenderer';
import type { PageElementData, PageSection, PageBlock } from './schema';
import { buildSectionStyle } from './sectionStyle';
import { isPageSectionEmptyForLearner, visiblePageBlocks } from './visibility';
import { ChevronDown, ChevronRight } from 'lucide-react';

export interface PagePlayerProps {
    /** Page data */
    data: PageElementData;
    /** When true, suppress page title (admin identifier synced from content Title field). */
    hideTitle?: boolean;
    /** Whether the activity has been submitted (controls showAfterSubmitted blocks) */
    submitted?: boolean;
    /** Additional CSS class */
    className?: string;
    /** Custom section renderer */
    renderSection?: (
        section: PageSection,
        index: number,
        defaultRender: React.ReactNode
    ) => React.ReactNode;
    /** Custom block renderer */
    renderBlock?: (
        block: PageBlock,
        index: number,
        defaultRender: React.ReactNode
    ) => React.ReactNode;
}

/**
 * Section component with optional collapsible behavior
 */
const SectionWrapper: React.FC<{
    section: PageSection;
    index: number;
    children: React.ReactNode;
}> = ({ section, index, children }) => {
    const settings = section.settings;
    const [isCollapsed, setIsCollapsed] = React.useState(settings?.defaultCollapsed ?? false);

    // If section is hidden, don't render
    if (settings?.hidden) {
        return null;
    }

    const sectionStyle = buildSectionStyle(settings);

    const hasTitle = section.title;
    const isCollapsible = settings?.collapsible && hasTitle;

    return (
        <div
            className={cn('page-player-section', settings?.className)}
            style={sectionStyle}
            data-section-id={section.id}
            data-section-index={index}
        >
            {hasTitle && (
                <div
                    className={cn(
                        'page-player-section-header flex items-center gap-2 mb-3',
                        isCollapsible && 'cursor-pointer hover:opacity-80'
                    )}
                    onClick={isCollapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
                >
                    {isCollapsible && (
                        <span className="text-muted-foreground">
                            {isCollapsed ? (
                                <ChevronRight className="h-4 w-4" />
                            ) : (
                                <ChevronDown className="h-4 w-4" />
                            )}
                        </span>
                    )}
                    <h3 className="text-lg font-semibold">{section.title}</h3>
                </div>
            )}
            {!isCollapsible || !isCollapsed ? (
                <div className="page-player-section-content">{children}</div>
            ) : null}
        </div>
    );
};

/**
 * Block wrapper component
 */
const BlockWrapper: React.FC<{
    block: PageBlock;
    index: number;
    submitted?: boolean;
    children: React.ReactNode;
}> = ({ block, index, submitted, children }) => {
    const settings = block.settings;

    if (settings?.hidden) {
        return null;
    }

    if (settings?.showAfterSubmitted && !submitted) {
        return null;
    }

    const blockStyle: React.CSSProperties = {};

    // Handle alignment
    if (settings?.alignment) {
        switch (settings.alignment) {
            case 'left':
                blockStyle.justifySelf = 'start';
                break;
            case 'center':
                blockStyle.justifySelf = 'center';
                break;
            case 'right':
                blockStyle.justifySelf = 'end';
                break;
            case 'stretch':
                blockStyle.justifySelf = 'stretch';
                break;
        }
    }

    // Handle grid span
    if (settings?.span) {
        blockStyle.gridColumn = `span ${settings.span}`;
    }

    return (
        <div
            className={cn('page-player-block', settings?.className)}
            style={blockStyle}
            data-block-id={block.id}
            data-block-index={index}
        >
            {children}
        </div>
    );
};

export const PagePlayer: React.FC<PagePlayerProps> = ({
    data,
    hideTitle = true,
    submitted,
    className,
    renderSection,
    renderBlock,
}) => {
    if (!data) {
        return (
            <div className={cn('page-player text-muted-foreground italic p-4', className)}>
                No page data available
            </div>
        );
    }

    const { title, description, sections = [] } = data;
    const showPageTitle = !hideTitle && Boolean(title?.trim());
    const showPageDescription = Boolean(description?.trim());

    // Render a single block
    const renderBlockContent = (block: PageBlock, blockIndex: number) => {
        const defaultRender = (
            <BlockWrapper block={block} index={blockIndex} submitted={submitted}>
                <BlockRenderer content={block.content} />
            </BlockWrapper>
        );

        if (renderBlock) {
            return renderBlock(block, blockIndex, defaultRender);
        }

        return defaultRender;
    };

    // Render a single section
    const renderSectionContent = (section: PageSection, sectionIndex: number) => {
        // EAA-2063: hidden blocks (audio script, rubric hints kept for the graders) are
        // skipped before any custom renderBlock sees them; a section left empty is omitted.
        if (isPageSectionEmptyForLearner(section)) return null;
        const defaultRender = (
            <SectionWrapper section={section} index={sectionIndex}>
                <LayoutContainer layout={section.layout}>
                    {visiblePageBlocks(section).map((block, blockIndex) => (
                        <React.Fragment key={block.id}>
                            {renderBlockContent(block, blockIndex)}
                        </React.Fragment>
                    ))}
                </LayoutContainer>
            </SectionWrapper>
        );

        if (renderSection) {
            return renderSection(section, sectionIndex, defaultRender);
        }

        return defaultRender;
    };

    return (
        <div className={cn('page-player', className)}>
            {/* Page Header — title is admin-only; instructions/description stay visible */}
            {(showPageTitle || showPageDescription) && (
                <div className="page-player-header space-y-1 pb-1">
                    {showPageTitle ? (
                        <h1 className="page-player-title text-2xl font-bold">{title}</h1>
                    ) : null}
                    {showPageDescription ? (
                        <p className="page-player-description text-muted-foreground mt-1">
                            {description}
                        </p>
                    ) : null}
                </div>
            )}

            {/* Sections — omit empty-state placeholder when there are no sections */}
            {sections.length > 0 ? (
                // EAA-2063: 12px between sections (was 24px) — sections of one page read as
                // one card, not separate boxes with large gaps between them.
                <div className="page-player-sections space-y-3">
                    {sections.map((section, index) => (
                        <React.Fragment key={section.id}>
                            {renderSectionContent(section, index)}
                        </React.Fragment>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

export default PagePlayer;
